import { createContext, useContext, useState, ReactNode, useMemo } from 'react'
import { useDebounce } from '@/hooks/useDebounce'
import { InventoryItem } from '../interfaces/inventory'
import { useContainersProductsQuery } from './useContainersProductsQuery'

interface LogisticInventoryContextType {
  inventoryData: InventoryItem[]
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  currentPage: number
  setCurrentPage: (_page: number) => void
  pageSize: number
  setPageSize: (_size: number) => void
  searchQuery: string
  setSearchQuery: (_query: string) => void
  refetch: () => void
  filters: Record<string, unknown>
  setFilters: (_filters: Record<string, unknown>) => void
  // Información de paginación del servidor
  totalItems: number
  totalPages: number
}

const LogisticInventoryContext = createContext<
  LogisticInventoryContextType | undefined
>(undefined)

interface LogisticInventoryProviderProps {
  children: ReactNode
}

export function LogisticInventoryProvider({
  children,
}: LogisticInventoryProviderProps) {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(20) // Valor por defecto sincronizado con backend
  const [filters, setFilters] = useState({})
  const [searchQuery, setSearchQuery] = useState('')

  // Debounce la búsqueda para evitar demasiadas llamadas al backend
  const debouncedSearchQuery = useDebounce(searchQuery, 300) // 300ms de delay optimizado

  // Resetear página a 1 cuando cambie la búsqueda (optimizado)
  const [previousSearch, setPreviousSearch] = useState('')
  if (debouncedSearchQuery !== previousSearch) {
    setPreviousSearch(debouncedSearchQuery)
    if (currentPage !== 1 && debouncedSearchQuery !== '') {
      setCurrentPage(1)
    }
  }

  // Parámetros para la query - usar pageSize seleccionado en el filtro
  const queryParams = useMemo(() => {
    const baseParams = {
      page: currentPage,
      limit: pageSize, // Usar exactamente la cantidad de filas por página seleccionada
      // Enviar búsqueda al backend cuando hay término de búsqueda
      ...(debouncedSearchQuery.trim() && {
        search: debouncedSearchQuery.trim(),
      }),
    }

    // Transform complex filters to API parameters
    const transformedFilters: Record<string, unknown> = {}

    // Handle supplier filter
    const supplierFilter = (filters as Record<string, unknown>).supplier
    if (supplierFilter && supplierFilter !== '' && supplierFilter !== 'todos') {
      transformedFilters.supplierIds = supplierFilter
    }

    // Handle expiration filter
    const expirationFilter = (filters as Record<string, unknown>).expiration as
      | {
          todos?: boolean
          urgente?: boolean
          alerta?: boolean
          normal?: boolean
        }
      | undefined
    if (expirationFilter && typeof expirationFilter === 'object') {
      if (!expirationFilter.todos) {
        // If not "todos", build expiration filter string
        const activeFilters = []
        if (expirationFilter.urgente) activeFilters.push('urgent')
        if (expirationFilter.alerta) activeFilters.push('alert')
        if (expirationFilter.normal) activeFilters.push('normal')
        if (activeFilters.length > 0) {
          transformedFilters.expiration = activeFilters.join(',')
        }
      }
    }

    return {
      ...baseParams,
      ...transformedFilters,
    }
  }, [currentPage, pageSize, debouncedSearchQuery, filters])

  // Usar el nuevo hook para containers/products
  const { inventoryData, pagination, isLoading, isFetching, isError, refetch } =
    useContainersProductsQuery({
      params: queryParams,
    })

  // Calcular total de páginas basado en la respuesta del servidor
  const totalItems = pagination?.total || 0
  const totalPages = Math.ceil(totalItems / pageSize)

  // El backend ahora maneja la búsqueda, no necesitamos filtrado local

  return (
    <LogisticInventoryContext.Provider
      value={{
        inventoryData: inventoryData || [],
        isLoading,
        isFetching,
        isError,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        searchQuery,
        setSearchQuery,
        refetch,
        filters,
        setFilters,
        totalItems,
        totalPages,
      }}
    >
      {children}
    </LogisticInventoryContext.Provider>
  )
}

export function useLogisticInventory() {
  const context = useContext(LogisticInventoryContext)
  if (context === undefined) {
    throw new Error(
      'useLogisticInventory must be used within a LogisticInventoryProvider'
    )
  }
  return context
}
